import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../env';
import { ImageAttribution } from '../../models/image-search';

/**
 * One image variant of a recipe image. Every upload yields three rows sharing
 * one asset — `thumbnail` (400px), `full` (1024px) and `original` — but the UI
 * works with the `full` variant as the single representation of a photo.
 * `owner_type` is `user` for uploads, `provider` for images pulled in during a
 * URL import, and `search` for images the user picked from an image search —
 * the last of which carries an `attribution` some licences require us to show.
 */
export interface RecipeImage {
  id?: number;
  url?: string;
  type?: 'thumbnail' | 'full' | 'original' | string;
  step_id?: number | null;
  is_primary?: boolean;
  owner_type?: 'user' | 'provider' | 'search' | string;
  /**
   * Set for images picked from an image search. Some provider licences (Pexels,
   * Open Food Facts) require this credit to be displayed next to the image.
   */
  attribution?: ImageAttribution;
  created_at?: string;
  updated_at?: string;
}

@Injectable({ providedIn: 'root' })
export class RecipeImageService {
  constructor(private http: HttpClient) {}

  /** GET /recipes/:id/images */
  getImages(recipeId: number): Observable<RecipeImage[]> {
    return this.http.get<RecipeImage[]>(`${environment.apiUrl}/recipes/${recipeId}/images`);
  }

  /**
   * POST /recipes/:id/images (multipart). When `isPrimary` is true the uploaded
   * image becomes the recipe's title image. The first image of a recipe is made
   * primary automatically by the backend regardless.
   */
  uploadImage(recipeId: number, file: File, isPrimary: boolean = false): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_primary', isPrimary.toString());
    return this.http.post(`${environment.apiUrl}/recipes/${recipeId}/images`, formData);
  }

  /** DELETE /recipe-images/:imageId (removes all variants of the photo). */
  deleteImage(imageId: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/recipe-images/${imageId}`);
  }

  /** PATCH /recipe-images/:imageId/is-primary — set the photo as the title. */
  setAsPrimaryImage(imageId: number): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/recipe-images/${imageId}/is-primary`, {});
  }
}
